interface OcrImageOptimizeOptions {
  maxDimension?: number
  targetMaxBytes?: number
  outputType?: "image/jpeg" | "image/webp"
  initialQuality?: number
  minQuality?: number
  qualityStep?: number
}

const DEFAULT_OPTIONS: Required<OcrImageOptimizeOptions> = {
  maxDimension: 2200,
  targetMaxBytes: 2 * 1024 * 1024,
  outputType: "image/jpeg",
  initialQuality: 0.9,
  minQuality: 0.68,
  qualityStep: 0.08,
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function getScaledSize(width: number, height: number, maxDimension: number): { width: number; height: number } {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height }
  }

  if (width > height) {
    const ratio = maxDimension / width
    return { width: maxDimension, height: Math.round(height * ratio) }
  }

  const ratio = maxDimension / height
  return { width: Math.round(width * ratio), height: maxDimension }
}

function changeFileExtension(fileName: string, extension: string): string {
  const lastDot = fileName.lastIndexOf(".")
  if (lastDot === -1) return `${fileName}.${extension}`
  return `${fileName.slice(0, lastDot)}.${extension}`
}

async function readImageFromFile(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file)

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("Image loading failed"))
      img.src = objectUrl
    })
    return image
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: "image/jpeg" | "image/webp", quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas conversion failed"))
          return
        }
        resolve(blob)
      },
      type,
      quality
    )
  })
}

export async function optimizeImageForOcr(
  file: File,
  options: OcrImageOptimizeOptions = {}
): Promise<File> {
  if (typeof window === "undefined" || !file.type.startsWith("image/")) {
    return file
  }

  const config = { ...DEFAULT_OPTIONS, ...options }

  if (file.size <= config.targetMaxBytes) {
    return file
  }

  try {
    const image = await readImageFromFile(file)
    const scaled = getScaledSize(image.width, image.height, config.maxDimension)
    const canvas = document.createElement("canvas")
    canvas.width = scaled.width
    canvas.height = scaled.height

    const context = canvas.getContext("2d")
    if (!context) return file

    // JPEG/WebP için transparan alanları beyaza sabitle.
    context.fillStyle = "#ffffff"
    context.fillRect(0, 0, scaled.width, scaled.height)
    context.drawImage(image, 0, 0, scaled.width, scaled.height)

    let quality = clamp(config.initialQuality, config.minQuality, 1)
    let blob = await canvasToBlob(canvas, config.outputType, quality)

    while (blob.size > config.targetMaxBytes && quality > config.minQuality) {
      quality = clamp(quality - config.qualityStep, config.minQuality, 1)
      blob = await canvasToBlob(canvas, config.outputType, quality)
      if (quality === config.minQuality) break
    }

    if (blob.size >= file.size * 0.98) {
      return file
    }

    const extension = config.outputType === "image/webp" ? "webp" : "jpg"
    const optimizedName = changeFileExtension(file.name, extension)
    return new File([blob], optimizedName, {
      type: config.outputType,
      lastModified: Date.now(),
    })
  } catch {
    return file
  }
}

export async function optimizeImagesForOcr(
  files: File[],
  options: OcrImageOptimizeOptions = {}
): Promise<File[]> {
  const optimized: File[] = []

  for (const file of files) {
    optimized.push(await optimizeImageForOcr(file, options))
  }

  return optimized
}
